/** @module What a copy cost: price, date and where it came from, as one reusable form part. */
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { SourceInput } from "@/components/suggested-inputs";
import { amountText, parseAmount, todayIso } from "@/lib/format";

/** The fields as typed; turned into API values only on save. */
export interface PurchaseDraft {
  price: string;
  date: string;
  source: string;
}

/** An empty purchase; the date starts at today, the usual case. */
export function newPurchase(): PurchaseDraft {
  return { price: "", date: todayIso(), source: "" };
}

export function draftOf(
  item: {
    purchase_price?: string | null;
    purchased_on?: string | null;
    purchase_source?: string | null;
  },
  locale: string,
): PurchaseDraft {
  return {
    price: amountText(item.purchase_price, locale),
    date: item.purchased_on ?? todayIso(),
    source: item.purchase_source ?? "",
  };
}

/** The API fields; null clears them. Undefined while the price is not a valid amount. */
export function purchaseBody(draft: PurchaseDraft):
  | {
      purchase_price: string | null;
      purchased_on: string | null;
      purchase_source: string | null;
    }
  | undefined {
  const price = parseAmount(draft.price);
  if (price === undefined) return undefined;
  return {
    purchase_price: price,
    // The prefilled date alone is no purchase: without a price it stays empty.
    purchased_on: price !== null && draft.date ? draft.date : null,
    purchase_source: draft.source.trim() || null,
  };
}

/** A euro amount field; marks itself invalid while the text is no amount. */
export function AmountInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const invalid = parseAmount(value) === undefined;
  return (
    <div className="field">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <span className="relative block">
        <input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          value={value}
          aria-invalid={invalid}
          aria-describedby={invalid ? `${id}-error` : undefined}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          className="input num w-full pr-9"
        />
        <span className="t-muted pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
          €
        </span>
      </span>
      {invalid ? (
        <span id={`${id}-error`} className="t-xs loss">
          {t("purchase.invalid")}
        </span>
      ) : null}
    </div>
  );
}

export function PurchaseFields({
  draft,
  onChange,
  priceLabel,
}: {
  draft: PurchaseDraft;
  onChange: (draft: PurchaseDraft) => void;
  priceLabel?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <AmountInput
          label={priceLabel ?? t("purchase.price")}
          value={draft.price}
          onChange={(price) => {
            onChange({ ...draft, price });
          }}
        />
        <div className="field">
          <label htmlFor={`${id}-date`} className="field-label">
            {t("purchase.date")}
          </label>
          <input
            id={`${id}-date`}
            type="date"
            value={draft.date}
            onChange={(event) => {
              onChange({ ...draft, date: event.target.value });
            }}
            className="input num w-full"
          />
        </div>
      </div>
      <SourceInput
        value={draft.source}
        onChange={(source) => {
          onChange({ ...draft, source });
        }}
      />
    </div>
  );
}
